import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTeams, TeamWithRole, TeamMembership } from '@/hooks/useTeams';
import { useAuth } from '@/contexts/AuthContext';

interface TeamContextType {
  teams: TeamWithRole[];
  activeTeam: TeamWithRole | null;
  setActiveTeam: (team: TeamWithRole | null) => void;
  isPersonalMode: boolean; // Explicit flag: true when no team is active
  loading: boolean;
  pendingInvitations: TeamMembership[];
  hasTeams: boolean;
  refetch: () => Promise<void>;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

const ACTIVE_TEAM_KEY = 'njreap_active_team_id';

export function TeamProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { teams, loading, pendingInvitations, refetch } = useTeams();
  const [activeTeam, setActiveTeamState] = useState<TeamWithRole | null>(null);

  // Load active team from localStorage on mount
  useEffect(() => {
    if (!user) {
      setActiveTeamState(null);
      return;
    }

    try {
      const savedTeamId = localStorage.getItem(ACTIVE_TEAM_KEY);
      if (savedTeamId && teams.length > 0) {
        const found = teams.find(t => t.id === savedTeamId);
        if (found) {
          setActiveTeamState(found);
        }
      }
    } catch {
      // localStorage may be unavailable (quota, privacy mode)
    }
  }, [user, teams]);

  const setActiveTeam = (team: TeamWithRole | null) => {
    setActiveTeamState(team);
    try {
      if (team) {
        localStorage.setItem(ACTIVE_TEAM_KEY, team.id);
      } else {
        localStorage.removeItem(ACTIVE_TEAM_KEY);
      }
    } catch {
      // localStorage may be unavailable
    }
  };

  // Clear active team if it's no longer in the teams list
  useEffect(() => {
    if (activeTeam && !teams.find(t => t.id === activeTeam.id)) {
      setActiveTeam(null);
    }
  }, [teams, activeTeam]);

  // Explicit flag for personal mode (no active team)
  const isPersonalMode = activeTeam === null;

  return (
    <TeamContext.Provider
      value={{
        teams,
        activeTeam,
        setActiveTeam,
        isPersonalMode,
        loading,
        pendingInvitations,
        hasTeams: teams.length > 0,
        refetch,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
}

export function useTeamContext() {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeamContext must be used within a TeamProvider');
  }
  return context;
}
